import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { Loader2, ExternalLink, Download } from "lucide-react";
import { useDashboardChart } from "@/hooks/api";
import { useCompanies } from "@/hooks/api/useCompanies";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "./multi-select";
import { ImpersonationReasonPicker } from "@/components/shared/impersonation-reason-picker";
import { Button } from "@/components/ui/button";
import {
  TableCell,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MaskedText } from "@/components/masked-text";
import { useAuth } from "@/lib/auth";
import { AutoChart } from "@/components/shared/auto-chart";
import { cn } from "@/lib/cn";
import { downloadCsv, filenameSlug, toCsv } from "@/lib/utils/csv";
import {
  DashboardTable,
  useDashboardTable,
  type DashboardTableColumn,
} from "./dashboard-table";
import { useDashboardUrlState } from "./useDashboardUrlState";
import { FiltersCard } from "./filters-card";

const DASHBOARD_ID = "b2b-lab-visits";

// Semicolon-separated: these files are opened in Excel on Norwegian locale,
// which reads comma-separated CSV as a single column.
const CSV_DELIMITER = ";";

const EMPLOYEE_CSV_HEADERS = [
  "Navn",
  "E-post",
  "Bedrift",
  "Lab",
  "Status",
  "Helseskjema",
  "Abonnement",
  "Opprettet",
  "Besoksdato",
];

const STATUS_LABELS: Record<string, string> = {
  paid: "Betalt",
  requestRejected: "Avvist",
  bloodTestRequested: "Blodprove bestilt",
  pendingRequisition: "Venter rekvirering",
  requisitioned: "Rekvirert",
  awaitingResults: "Venter resultater",
  allBiometricResultsReceived: "Resultater mottatt",
  assessmentCompleted: "Vurdering fullfort",
};

function isoDate(d: Date): string {
  return d.toISOString().split("T")[0]!;
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return isoDate(d);
}

const PERIOD_PRESETS: { label: string; from: string; to: string }[] = (() => {
  const now = new Date();
  const today = isoDate(now);
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed

  return [
    { label: "Fra start", from: "2026-05-01", to: today },
    {
      label: "Denne maneden",
      from: `${year}-${String(month + 1).padStart(2, "0")}-01`,
      to: today,
    },
    {
      label: "Forrige maned",
      from: `${month === 0 ? year - 1 : year}-${String(month === 0 ? 12 : month).padStart(2, "0")}-01`,
      to: `${year}-${String(month + 1).padStart(2, "0")}-01`,
    },
    { label: "I ar", from: `${year}-01-01`, to: today },
    { label: "I fjor", from: `${year - 1}-01-01`, to: `${year - 1}-12-31` },
  ];
})();

type VisitsOverTimeRow = {
  day: string;
  started: number | string;
  completed: number | string;
};

type CompanyTotalsRow = {
  company_name: string;
  company_id: string;
  started: number | string;
  completed: number | string;
  unique_employees: number | string;
  blood_test_requested: number | string;
  pending_requisition: number | string;
  requisitioned: number | string;
  awaiting_results: number | string;
  results_received: number | string;
  assessment_completed: number | string;
  other_statuses: number | string;
};

type StatusBreakdownRow = {
  status: string;
  count: number | string;
};

type EmployeeRow = {
  given_name: string | null;
  family_name: string | null;
  email: string | null;
  health_survey_completed: boolean;
  company_name: string;
  lab_name: string | null;
  status: string;
  created_at: string;
  visit_date: string | null;
  auth_user_id: string;
  has_active_subscription: boolean;
  had_subscription_before: boolean;
};

type ImpersonationTarget = {
  authUserId: string;
  fullName: string;
};

/**
 * The "Ansatte" table's columns are the dashboard's only column filters:
 * status, lab and the two badge columns are closed sets. "Bedrift" is
 * left out on purpose — the page already has a company filter above, and
 * a second one in the header would just compete with it.
 */
function employeeColumns(singleCompany: boolean): DashboardTableColumn<EmployeeRow>[] {
  const columns: DashboardTableColumn<EmployeeRow>[] = [
    { id: "name", label: "Navn", value: (r) => employeeName(r) },
    { id: "email", label: "E-post", value: (r) => r.email },
  ];
  if (!singleCompany) {
    columns.push({ id: "company_name", label: "Bedrift", value: (r) => r.company_name });
  }
  columns.push(
    { id: "lab_name", label: "Lab", value: (r) => r.lab_name, filterable: true },
    {
      id: "status",
      label: "Status",
      value: (r) => r.status,
      filterable: true,
      formatOption: (v) => STATUS_LABELS[v] ?? v,
    },
    {
      id: "health_survey_completed",
      label: "Helseskjema",
      value: (r) => r.health_survey_completed,
      filterable: true,
      formatOption: (v) => (v === "true" ? "Fullfort" : "Ikke fullfort"),
    },
    {
      id: "subscription",
      label: "Abonnement",
      value: (r) =>
        subscriptionLabel(r.has_active_subscription, r.had_subscription_before),
      filterable: true,
    },
    { id: "created_at", label: "Opprettet", value: (r) => r.created_at },
    { id: "visit_date", label: "Besoksdato", value: (r) => r.visit_date },
    { id: "actions", label: "", sortable: false },
  );
  return columns;
}

const COMPANY_TOTALS_COLUMNS: DashboardTableColumn<CompanyTotalsRow>[] = [
  { id: "company_name", label: "Bedrift", value: (r) => r.company_name },
  { id: "started", label: "Påbegynt", value: (r) => r.started, numeric: true, align: "right" },
  { id: "completed", label: "Gjennomført", value: (r) => r.completed, numeric: true, align: "right" },
  { id: "unique_employees", label: "Ansatte", value: (r) => r.unique_employees, numeric: true, align: "right" },
  { id: "blood_test_requested", label: "Bestilt", value: (r) => r.blood_test_requested, numeric: true, align: "right" },
  { id: "pending_requisition", label: "Venter rekv.", value: (r) => r.pending_requisition, numeric: true, align: "right" },
  { id: "requisitioned", label: "Rekvirert", value: (r) => r.requisitioned, numeric: true, align: "right" },
  { id: "awaiting_results", label: "Venter res.", value: (r) => r.awaiting_results, numeric: true, align: "right" },
  { id: "results_received", label: "Res. mottatt", value: (r) => r.results_received, numeric: true, align: "right" },
  { id: "assessment_completed", label: "Vurdering", value: (r) => r.assessment_completed, numeric: true, align: "right" },
];

const STATUS_BREAKDOWN_COLUMNS: DashboardTableColumn<StatusBreakdownRow>[] = [
  {
    id: "status",
    label: "Status",
    value: (r) => STATUS_LABELS[r.status] ?? r.status,
  },
  { id: "count", label: "Antall", value: (r) => r.count, numeric: true, align: "right" },
];

export function B2bLabVisitsDashboard() {
  const [{ from, to, employeeSearch }, update] = useDashboardUrlState({
    from: "2026-05-01",
    to: isoDaysAgo(0),
    employeeSearch: "",
  });
  const [searchParams, setSearchParams] = useSearchParams();
  // Repeated params preserve multi-selection; old single-company links still work.
  const companyIds = useMemo(
    () => (searchParams.has("companyIds")
      ? searchParams.getAll("companyIds")
      : searchParams.getAll("companyId")).filter(Boolean),
    [searchParams],
  );
  const singleCompany = companyIds.length === 1;

  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [reasonTarget, setReasonTarget] = useState<ImpersonationTarget | null>(
    null,
  );
  const [reason, setReason] = useState("");
  const [impersonating, setImpersonating] = useState(false);

  const openReasonDialog = (target: ImpersonationTarget) => {
    setReason("");
    setReasonTarget(target);
  };

  const confirmImpersonate = async (selectedReason: string) => {
    const reason = selectedReason.trim();
    if (!reasonTarget || impersonating || !reason) return;
    const { authUserId } = reasonTarget;
    setReasonTarget(null);
    setImpersonating(true);
    try {
      await impersonate(authUserId, reason);
      navigate(`/anamnesis/${authUserId}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunne ikke impersonere bruker");
    } finally {
      setImpersonating(false);
    }
  };

  const { data: companies } = useCompanies();
  const companyOptions = useMemo(() => {
    const options = (companies ?? []).map((c) => ({ value: c.id, label: c.name }));
    // Keep stale link selections visible and removable, not disguised as "all".
    for (const id of companyIds) {
      if (!options.some((option) => option.value === id)) {
        options.push({ value: id, label: `Ukjent bedrift (${id})` });
      }
    }
    return options;
  }, [companies, companyIds]);

  const filterParams = useMemo(
    () => ({ from, to, companyIds: companyIds.length ? companyIds : undefined }),
    [from, to, companyIds],
  );

  const visitsOverTime = useDashboardChart<
    typeof filterParams,
    VisitsOverTimeRow
  >(DASHBOARD_ID, "visits-over-time", filterParams);

  const companyTotals = useDashboardChart<typeof filterParams, CompanyTotalsRow>(
    DASHBOARD_ID,
    "company-totals",
    filterParams,
  );

  const statusBreakdown = useDashboardChart<
    typeof filterParams,
    StatusBreakdownRow
  >(DASHBOARD_ID, "status-breakdown", filterParams);

  const employeesForCompany = useDashboardChart<
    typeof filterParams,
    EmployeeRow
  >(DASHBOARD_ID, "employees-for-company", filterParams);

  const companyTotalsTable = useDashboardTable(
    "company-totals",
    COMPANY_TOTALS_COLUMNS,
    companyTotals.data
  );
  const statusBreakdownTable = useDashboardTable(
    "status-breakdown",
    STATUS_BREAKDOWN_COLUMNS,
    statusBreakdown.data
  );
  const searchedEmployees = useMemo(
    () => employeesForCompany.data?.filter((row) => employeeMatchesName(row, employeeSearch)),
    [employeesForCompany.data, employeeSearch],
  );
  const employeesTable = useDashboardTable(
    "employees",
    employeeColumns(singleCompany),
    searchedEmployees
  );

  const selectedCompanyName = useMemo(() => {
    if (!companyIds.length) return null;
    return companyIds.map((id) => companies?.find((c) => c.id === id)?.name ?? id).join(", ");
  }, [companyIds, companies]);

  // Exports exactly the rows the "Ansatte" table is showing — same column
  // filters, same sort, same labels. "Bedrift" is always a column here (the
  // table hides it when a single company is selected) so the file stands on
  // its own once downloaded.
  const exportEmployees = () => {
    const rows = employeesTable.viewRows;
    if (rows.length === 0) return;

    const csv = toCsv(
      EMPLOYEE_CSV_HEADERS,
      rows.map((row) => [
        employeeName(row),
        row.email,
        row.company_name,
        row.lab_name,
        STATUS_LABELS[row.status] ?? row.status,
        healthSurveyLabel(row.health_survey_completed),
        subscriptionLabel(
          row.has_active_subscription,
          row.had_subscription_before,
        ),
        row.created_at,
        row.visit_date,
      ]),
      CSV_DELIMITER,
    );

    const company = singleCompany && selectedCompanyName
      ? filenameSlug(selectedCompanyName)
      : companyIds.length ? "valgte-bedrifter" : "alle-bedrifter";
    downloadCsv(csv, `ansatte-${company}-${from}-${to}.csv`, { bom: true });
    toast.success(`${rows.length} ansatte eksportert`);
  };

  const kpiRows = companyTotals.data;

  return (
    <div className="space-y-6">
      <FiltersCard>
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Fra</span>
            <Input
              type="date"
              value={from}
              onChange={(e) => update({ from: e.target.value })}
              className="w-44"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Til</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => update({ to: e.target.value })}
              className="w-44"
            />
          </label>
          <div className="space-y-1 flex-1 min-w-[12rem]">
            <span className="text-xs text-admin-text-subtle">Bedrift</span>
            <MultiSelect
              options={companyOptions}
              selected={companyIds}
              placeholder="Alle bedrifter"
              searchPlaceholder="Søk bedrift…"
              onChange={(next) => setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.delete("companyId");
                params.delete("companyIds");
                for (const id of next) params.append("companyIds", id);
                return params;
              }, { replace: true })}
            />
          </div>
        </div>
        <p className="text-xs text-admin-text-secondary">
          Fra og Til er inklusive. Påbegynt, ansatte og statusfordeling filtreres på
          opprettelsesdato. Gjennomført filtreres på labbesøksdato (dato for blodprøven).
          Besøk over tid og Per bedrift viser begge datotypene.
        </p>
        <div className="flex flex-wrap gap-2">
          {PERIOD_PRESETS.map((p) => (
            <Button
              key={p.label}
              variant={from === p.from && to === p.to ? "default" : "outline"}
              size="sm"
              onClick={() => update({ from: p.from, to: p.to })}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </FiltersCard>

      {/* KPI summary from company-totals.
          "Påbegynt" = created_at in range. "Gjennomført" = visit_date in range
          (different cohorts — a row can be in both, only one, or neither).
          KPIs reflect the selected companies.
          When all are shown, "Ansatte" sums per-company unique_employees, so
          a person at two companies counts twice. */}
      {kpiRows && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            label="Påbegynt"
            value={String(kpiRows.reduce((s, r) => s + Number(r.started), 0))}
          />
          <KpiCard
            label="Gjennomført"
            value={String(kpiRows.reduce((s, r) => s + Number(r.completed), 0))}
          />
          <KpiCard label="Bedrifter" value={String(kpiRows.length)} />
          <KpiCard
            label={singleCompany ? "Ansatte" : "Ansatte (sum per bedrift)"}
            value={String(
              kpiRows.reduce((s, r) => s + Number(r.unique_employees), 0),
            )}
          />
        </div>
      )}

      {/* Visits over time chart — two lines: Påbegynt (created_at) and Gjennomført (visit_date) */}
      <Card>
        <CardHeader>
          <CardTitle>Besok over tid</CardTitle>
        </CardHeader>
        <CardContent>
          {visitsOverTime.isLoading ? (
            <ChartSkeleton />
          ) : visitsOverTime.isError ? (
            <ErrorBlock message={visitsOverTime.error?.message} />
          ) : !visitsOverTime.data || visitsOverTime.data.length === 0 ? (
            <EmptyBlock>Ingen B2B-labbesok i perioden.</EmptyBlock>
          ) : (
            <AutoChart
              rows={visitsOverTime.data as Record<string, unknown>[]}
            />
          )}
        </CardContent>
      </Card>

      {/* Company totals table */}
      <Card>
        <CardHeader>
          <CardTitle>Per bedrift</CardTitle>
        </CardHeader>
        <CardContent>
          {companyTotals.isLoading ? (
            <ChartSkeleton />
          ) : companyTotals.isError ? (
            <ErrorBlock message={companyTotals.error?.message} />
          ) : !companyTotals.data || companyTotals.data.length === 0 ? (
            <EmptyBlock>Ingen bedrifter med labbesok i perioden.</EmptyBlock>
          ) : (
            <DashboardTable
              table={companyTotalsTable}
              columns={COMPANY_TOTALS_COLUMNS}
              renderRow={(row) => (
                <TableRow key={row.company_id}>
                  <TableCell className="font-medium text-admin-text-primary">
                    {row.company_name}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {String(row.started)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {String(row.completed)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {String(row.unique_employees)}
                  </TableCell>
                  <StatusCell value={row.blood_test_requested} />
                  <StatusCell value={row.pending_requisition} />
                  <StatusCell value={row.requisitioned} />
                  <StatusCell value={row.awaiting_results} />
                  <StatusCell value={row.results_received} />
                  <StatusCell value={row.assessment_completed} />
                </TableRow>
              )}
            />
          )}
        </CardContent>
      </Card>

      {/* Status breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>
            Statusfordeling
            {selectedCompanyName ? ` — ${selectedCompanyName}` : ""}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {statusBreakdown.isLoading ? (
            <ChartSkeleton />
          ) : statusBreakdown.isError ? (
            <ErrorBlock message={statusBreakdown.error?.message} />
          ) : !statusBreakdown.data || statusBreakdown.data.length === 0 ? (
            <EmptyBlock>Ingen data.</EmptyBlock>
          ) : (
            <DashboardTable
              table={statusBreakdownTable}
              columns={STATUS_BREAKDOWN_COLUMNS}
              renderRow={(row) => (
                <TableRow key={row.status}>
                  <TableCell className="text-admin-text-primary">
                    {STATUS_LABELS[row.status] ?? row.status}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {String(row.count)}
                  </TableCell>
                </TableRow>
              )}
            />
          )}
        </CardContent>
      </Card>

      {/* Employees list */}
      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>
            Ansatte{selectedCompanyName ? ` — ${selectedCompanyName}` : ""}
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={exportEmployees}
            disabled={employeesTable.viewRows.length === 0}
          >
            <Download className="h-3 w-3" />
            Eksporter CSV
          </Button>
        </CardHeader>
        <CardContent>
          <label className="mb-4 block max-w-sm space-y-1">
            <span className="text-xs text-admin-text-subtle">Søk på navn</span>
            <Input
              type="search"
              value={employeeSearch}
              onChange={(e) => update({ employeeSearch: e.target.value })}
              placeholder="Søk i Navn…"
            />
          </label>
          {employeesForCompany.isLoading ? (
            <ChartSkeleton />
          ) : employeesForCompany.isError ? (
            <ErrorBlock message={employeesForCompany.error?.message} />
          ) : !employeesForCompany.data ||
            employeesForCompany.data.length === 0 ? (
            <EmptyBlock>Ingen ansatte med labbesok i perioden.</EmptyBlock>
          ) : searchedEmployees?.length === 0 ? (
            <EmptyBlock>Ingen ansatte matcher navnesøket.</EmptyBlock>
          ) : (
            <DashboardTable
              table={employeesTable}
              columns={employeeColumns(singleCompany)}
              renderRow={(row, i) => (
                <TableRow key={`${row.auth_user_id}-${i}`}>
                  <TableCell>{employeeName(row) || "—"}</TableCell>
                  <TableCell className="text-admin-text-secondary">
                    {row.email ?? "—"}
                  </TableCell>
                  {!singleCompany && (
                    <TableCell className="text-admin-text-secondary">
                      {row.company_name}
                    </TableCell>
                  )}
                  <TableCell className="text-admin-text-secondary">
                    {row.lab_name ?? "—"}
                  </TableCell>
                  <TableCell>
                    {STATUS_LABELS[row.status] ?? row.status}
                  </TableCell>
                  <TableCell>
                    <HealthSurveyBadge
                      completed={row.health_survey_completed}
                    />
                  </TableCell>
                  <TableCell>
                    <SubscriptionBadge
                      active={row.has_active_subscription}
                      past={row.had_subscription_before}
                    />
                  </TableCell>
                  <TableCell className="tabular-nums whitespace-nowrap">
                    {row.created_at ?? "—"}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {row.visit_date ?? "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7"
                      disabled={impersonating}
                      onClick={() =>
                        openReasonDialog({
                          authUserId: row.auth_user_id,
                          fullName:
                            employeeName(row) ||
                            row.email ||
                            row.auth_user_id,
                        })
                      }
                    >
                      {impersonating ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <ExternalLink className="h-3 w-3" />
                      )}
                      Anamnese
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            />
          )}
        </CardContent>
      </Card>

      {/* Impersonation reason dialog */}
      <Dialog
        open={!!reasonTarget}
        onOpenChange={(open) => {
          if (!open) setReasonTarget(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Grunn for impersonering</DialogTitle>
          </DialogHeader>
          {reasonTarget && (
            <div className="space-y-3">
              <p className="text-sm text-admin-text-secondary">
                Impersonerer{" "}
                <span className="font-medium text-admin-text-primary">
                  <MaskedText value={reasonTarget.fullName} />
                </span>
              </p>
              <ImpersonationReasonPicker
                value={reason}
                onChange={setReason}
                onConfirm={confirmImpersonate}
                disabled={impersonating}
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setReasonTarget(null)}>
                  Avbryt
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="px-4 py-3">
        <p className="text-xs text-admin-text-subtle">{label}</p>
        <p className="text-lg font-bold text-admin-text-primary">{value}</p>
      </CardContent>
    </Card>
  );
}

function healthSurveyLabel(completed: boolean): string {
  return completed ? "Fullfort" : "Ikke fullfort";
}

function subscriptionLabel(active: boolean, past: boolean): string {
  if (active) return "Aktiv";
  if (past) return "Tidligere";
  return "Ingen";
}

function employeeName(row: Pick<EmployeeRow, "given_name" | "family_name">): string {
  return [row.given_name, row.family_name].filter(Boolean).join(" ");
}

export function employeeMatchesName(
  row: Pick<EmployeeRow, "given_name" | "family_name">,
  search: string,
): boolean {
  return employeeName(row).toLocaleLowerCase("nb").includes(search.trim().toLocaleLowerCase("nb"));
}

const BADGE_BASE =
  "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";

function HealthSurveyBadge({ completed }: { completed: boolean }) {
  return (
    <span
      className={cn(
        BADGE_BASE,
        completed
          ? "bg-admin-mint-500/10 text-admin-mint-400"
          : "bg-zinc-500/10 text-admin-text-subtle",
      )}
    >
      {healthSurveyLabel(completed)}
    </span>
  );
}

function SubscriptionBadge({
  active,
  past,
}: {
  active: boolean;
  past: boolean;
}) {
  return (
    <span
      className={cn(
        BADGE_BASE,
        active
          ? "bg-admin-mint-500/10 text-admin-mint-400"
          : past
            ? "bg-admin-warn-500/10 text-admin-warn-400"
            : "bg-zinc-500/10 text-admin-text-subtle",
      )}
    >
      {subscriptionLabel(active, past)}
    </span>
  );
}

function StatusCell({ value }: { value: number | string }) {
  const n = Number(value);
  return (
    <TableCell
      className={`text-right tabular-nums ${n === 0 ? "text-admin-text-subtle" : ""}`}
    >
      {n === 0 ? "—" : String(n)}
    </TableCell>
  );
}

function ChartSkeleton() {
  return (
    <div className="flex items-center gap-2 text-admin-text-subtle text-sm py-8 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" />
      Laster…
    </div>
  );
}

function EmptyBlock({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm text-admin-text-subtle py-6 text-center">
      {children}
    </p>
  );
}

function ErrorBlock({ message }: { message?: string }) {
  return (
    <div className="rounded-md border border-admin-danger-500/30 bg-admin-danger-500/5 p-4 text-sm text-admin-danger-400">
      {message || "Ukjent feil"}
    </div>
  );
}
